// PhotoRoll (job page round 3, Amir 26 Oct: "do it the way Instagram does, tile them, with a timestamp
// under each one; full width, scrolling left and right"): every photo on a record in one line of square
// tiles, newest first, each with when it was taken under it. The line is as wide as its section and
// scrolls sideways, snapping to a tile; the tiles are buttons in the tab order, ← and → move between
// them (Home and End to the ends) and bring the one focused into view; a press opens the viewer. No
// counts and no labels on a tile: what a photo is, where it belongs and who sees it are the viewer's.
// None: one muted line. One on its way up is a dashed tile with its words (PhotoThumb's waiting).
// A new component, not PhotoGroup or a PhotoStrip: PhotoGroup is one visit's photos by stage, each stage
// named with its count, wrapping; the two PhotoStrips are a closed line's few small thumbnails and the
// round-1 sheet's grid with Show all. None is one sideways line of square tiles with a time under each.
// Reads only --roll-*; PhotoThumb reads its own.
import type { KeyboardEvent } from 'react';
import { PhotoThumb, type Photo } from '../PhotoThumb/PhotoThumb.tsx';
import './PhotoRoll.css';

export interface PhotoRollProps {
  /** Newest first; each with `taken` ("Sun Oct 25, 9:24 a.m.") for the line under its tile. */
  photos: Photo[];
  /** A tile was pressed: open the viewer at that photo. */
  onOpen?: (photoId: string) => void;
  /** Its name for screen readers ("The job’s photos, newest first"). */
  label: string;
  /** What it says with none. */
  emptyText?: string;
}

const KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];

export function PhotoRoll({ photos, onOpen, label, emptyText = 'No photos yet' }: PhotoRollProps) {
  if (!photos.length) return <p className="fl-roll__empty">{emptyText}</p>;
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (!KEYS.includes(e.key)) return;
    const tiles = [...e.currentTarget.querySelectorAll<HTMLElement>('button.fl-thumb')];
    const i = tiles.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    const j =
      e.key === 'ArrowRight'
        ? Math.min(i + 1, tiles.length - 1)
        : e.key === 'ArrowLeft'
          ? Math.max(i - 1, 0)
          : e.key === 'Home'
            ? 0
            : tiles.length - 1;
    e.preventDefault();
    tiles[j]?.focus({ preventScroll: true });
    tiles[j]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  return (
    <ul className="fl-roll" aria-label={label} onKeyDown={onKeyDown}>
      {photos.map((p) => (
        <li key={p.id} className="fl-roll__item">
          <PhotoThumb photo={p} size="tile" onPress={onOpen} />
          {p.taken ? <span className="fl-roll__when">{p.taken}</span> : null}
        </li>
      ))}
    </ul>
  );
}
